/**
 * Pathname → surface.
 *
 * A surface is the part of the product somebody was in, not the URL they were
 * at. `/tickets/4f3c…/edit` and `/tickets` are both "tickets"; storing the raw
 * path would put record ids in an analytics table (they are customer data, and
 * they are also what makes a dedupe key useless).
 *
 * Pure and exported so it can be tested without a request.
 */

/** Paths that are not product usage and must never produce a row. */
const IGNORED_PREFIXES = [
  "/api/",
  "/auth/",
  "/_next/",
  "/oauth/",
  "/.well-known/",
  "/s/", // public secure links — the visitor is not a signed-in user
];

/**
 * First segment → surface name. Anything not listed falls back to its own first
 * segment, so a new page is categorized correctly the day it ships rather than
 * landing in "other" until somebody remembers this file.
 */
const SURFACE_ALIASES: Readonly<Record<string, string>> = {
  "": "dashboard",
  dashboard: "dashboard",
  "dashboard-unified": "dashboard",
  contacts: "crm",
  companies: "crm",
  deals: "crm",
  crm: "crm",
  search: "search",
  chat: "chat",
  tickets: "tickets",
  invoices: "invoices",
  "recurring-charges": "invoices",
  contracts: "contracts",
  documents: "documents",
  "msp-docs": "documents",
  reports: "reports",
  tasks: "tasks",
  "time-tracking": "time-tracking",
  account: "account",
  admin: "admin",
  mktg: "marketing",
  campaigns: "marketing",
  "social-media-posts": "marketing",
  projects: "projects",
  routines: "routines",
  sequences: "sales",
  offerings: "sales",
};

export function shouldRecordPath(pathname: string): boolean {
  if (!pathname.startsWith("/")) return false;
  return !IGNORED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function surfaceForPath(pathname: string): string {
  const [, first = ""] = pathname.split("/");
  const alias = SURFACE_ALIASES[first];
  if (alias) return alias;
  // A bare segment, lowercased and stripped of anything that is not a word
  // character — a uuid segment can never be a surface name.
  const cleaned = first.toLowerCase().replace(/[^a-z0-9-]/g, "");
  return cleaned.length > 0 && cleaned.length <= 40 ? cleaned : "other";
}
